import { v4 as uuidv4 } from 'uuid';

import appConfig from '@/config';

const FALLBACK_CURRENCIES = ['EUR', 'USD', 'GBP'];

const configuredCurrencyOptions = Array.isArray(appConfig?.forms?.currency_options)
  ? appConfig.forms.currency_options
  : FALLBACK_CURRENCIES;

export const SUPPORT_CURRENCY_OPTIONS = configuredCurrencyOptions
  .map(option => (typeof option === 'string' ? option.toUpperCase() : ''))
  .filter(Boolean);

export const DEFAULT_SUPPORT_CURRENCY = SUPPORT_CURRENCY_OPTIONS.includes('EUR')
  ? 'EUR'
  : (SUPPORT_CURRENCY_OPTIONS[0] || 'EUR');

export const SUPPORT_TITLE = 'Support';
export const SUPPORT_PITCH_MAX_LENGTH = 700;

export function withSoftHyphens(value) {
  if (typeof value !== 'string' || value.length === 0) {
    return value || '';
  }

  let result = '';
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    result += char;
    if (char.trim()) {
      result += '\u00AD';
    }
  }
  return result;
}

const CURRENCY_SYMBOL_MAP = {
  EUR: '\u20AC',
  USD: '$',
  GBP: '\u00A3',
};

export function normaliseSupportCurrency(value) {
  const candidate = typeof value === 'string' ? value.toUpperCase() : '';
  return SUPPORT_CURRENCY_OPTIONS.includes(candidate) ? candidate : DEFAULT_SUPPORT_CURRENCY;
}

export function getSupportCurrencySymbol(value) {
  const currency = normaliseSupportCurrency(value);
  return CURRENCY_SYMBOL_MAP[currency] || currency;
}

/**
 * Helper factory to create a new tier with sensible defaults.
 * Using a function avoids sharing references when cloning the default configuration.
 */
export function createEmptyTier() {
  return {
    id: uuidv4(),
    label: '',
    amount: 5,
    note: ''
  };
}

export function createDefaultAmounts() {
  return [
    { id: uuidv4(), amount: '', isCustom: false },
    { id: uuidv4(), amount: '', isCustom: false },
    { id: uuidv4(), amount: '', isCustom: false },
    { id: 'custom', amount: '', isCustom: true }
  ];
}

/**
 * Default support configuration applied when the profile has not customised the feature yet.
 */
export const defaultSupportConfig = {
  enabled: false,
  title: SUPPORT_TITLE,
  pitch: '',
  currency: DEFAULT_SUPPORT_CURRENCY,
  backgroundImage: null,
  tiers: [createEmptyTier(), createEmptyTier()],
  defaultAmounts: createDefaultAmounts(),
  allowCustomAmount: true,
  goal: {
    amount: '',
    label: ''
  },
  currencySymbol: getSupportCurrencySymbol(DEFAULT_SUPPORT_CURRENCY),
};

function buildBackgroundPreview(imageId) {
  if (!imageId) {
    return null;
  }

  const rawValue = typeof imageId === 'string'
    ? imageId
    : imageId?.image_id || imageId?.id || imageId?.url || '';

  if (!rawValue) {
    return null;
  }

  if (/^(https?:\/\/|blob:|data:)/i.test(rawValue)) {
    return rawValue;
  }

  const baseUrl = appConfig?.app?.imageServiceDomain;
  if (!baseUrl) {
    return rawValue;
  }

  return `${baseUrl}/${rawValue}/w=1600`;
}

function mapBackendSupportConfig(rawConfig) {
  if (!rawConfig) {
    return rawConfig;
  }

  const hasBackendKeys = [
    'support_enabled',
    'support_title',
    'support_pitch',
    'support_background_image',
    'support_tiers',
    'support_default_amounts',
    'support_goal',
    'support_currency'
  ].some(key => Object.prototype.hasOwnProperty.call(rawConfig, key));

  if (!hasBackendKeys) {
    return rawConfig;
  }

  const backgroundSource = rawConfig.support_background_image
    ?? rawConfig.background_image
    ?? rawConfig.backgroundImage;
  const backgroundId = typeof backgroundSource === 'object'
    ? backgroundSource?.image_id || backgroundSource?.id || backgroundSource?.url || ''
    : backgroundSource;

  let tiers;
  if (Array.isArray(rawConfig.support_tiers)) {
    tiers = rawConfig.support_tiers
      .map((tier, index) => {
        const rawAmount = tier?.tier_amount ?? tier?.amount ?? tier?.value ?? '';
        const numericAmount = Number(rawAmount);
        const resolvedAmount =
          rawAmount === '' || rawAmount === null || Number.isNaN(numericAmount) ? '' : numericAmount;

        return {
          id: uuidv4(),
          label: tier?.tier_label ?? tier?.label ?? tier?.tier_description ?? tier?.description ?? '',
          amount: resolvedAmount,
          note: tier?.tier_note ?? tier?.note ?? '',
          order: tier?.tier_order ?? tier?.order ?? index,
        };
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  let defaultAmounts;
  if (Array.isArray(rawConfig.support_default_amounts)) {
    defaultAmounts = rawConfig.support_default_amounts.map((amount, index) => {
      if (amount === null || amount === undefined || amount === '') {
        return { id: uuidv4(), amount: '', order: index, isCustom: false };
      }

      const numeric = Number(amount);
      return {
        id: uuidv4(),
        amount: Number.isNaN(numeric) ? '' : numeric,
        order: index,
        isCustom: false,
      };
    });
  }

  const currencyCodeRaw = (rawConfig.support_currency ?? rawConfig.currency ?? 'eur').toString().trim();
  const currencyCode = currencyCodeRaw ? currencyCodeRaw.toUpperCase() : 'EUR';
  const currencySymbol = (rawConfig.support_currency_symbol ?? rawConfig.currency_symbol)
    ?? (CURRENCY_SYMBOL_MAP[currencyCode] || '\u20AC');

  const goal = rawConfig.support_goal
    ? {
      label: rawConfig.support_goal.goal_label ?? rawConfig.support_goal.label ?? '',
      amount: rawConfig.support_goal.goal_target_amount
          ?? rawConfig.support_goal.target_amount
          ?? rawConfig.support_goal.amount
          ?? '',
      currentAmount: rawConfig.support_goal.goal_current_amount
          ?? rawConfig.support_goal.current_amount
          ?? rawConfig.support_goal.currentAmount
          ?? rawConfig.support_goal.current
          ?? 0,
    }
    : undefined;

  return {
    enabled: rawConfig.support_enabled ?? false,
    title: SUPPORT_TITLE,
    pitch: rawConfig.support_pitch ?? rawConfig.pitch ?? '',
    backgroundImage: backgroundId ? { id: backgroundId, previewUrl: buildBackgroundPreview(backgroundId) } : null,
    tiers,
    defaultAmounts,
    allowCustomAmount: true,
    goal,
    currency: currencyCode,
    currencySymbol,
  };
}

/**
 * Small helper to normalise raw data received from the API so the UI can work safely.
 */
export function normaliseSupportConfig(rawConfig) {
  const config = mapBackendSupportConfig(rawConfig);

  if (!config) {
    return {
      ...defaultSupportConfig,
      tiers: defaultSupportConfig.tiers.map(tier => ({ ...tier, id: uuidv4() })),
      defaultAmounts: createDefaultAmounts()
    };
  }

  const incomingDefaultAmounts = Array.isArray(config.defaultAmounts) ? config.defaultAmounts : [];

  const normalisedDefaultAmounts = createDefaultAmounts().map((slot, index) => {
    if (slot.isCustom) {
      const existingCustom = incomingDefaultAmounts.find(item => item && item.isCustom);
      return {
        ...slot,
        ...(existingCustom || {}),
        id: existingCustom?.id || slot.id,
        isCustom: true
      };
    }

    const candidate = incomingDefaultAmounts[index] ?? incomingDefaultAmounts.find(item => item && !item.isCustom && item?.order === index);
    const resolvedAmount = candidate && typeof candidate === 'object' ? candidate.amount : candidate;

    return {
      ...slot,
      id: typeof candidate === 'object' && candidate?.id ? candidate.id : slot.id,
      amount: resolvedAmount === undefined || resolvedAmount === null ? '' : String(resolvedAmount),
      isCustom: false
    };
  });

  const resolvedCurrency = normaliseSupportCurrency(config.currency);

  return {
    ...defaultSupportConfig,
    ...config,
    title: SUPPORT_TITLE,
    currency: resolvedCurrency,
    tiers: Array.isArray(config.tiers) && config.tiers.length > 0
      ? config.tiers.map(tier => ({ ...createEmptyTier(), ...tier, id: tier.id || uuidv4() })).slice(0, 3)
      : defaultSupportConfig.tiers.map(tier => ({ ...tier, id: uuidv4() })),
    defaultAmounts: normalisedDefaultAmounts,
    goal: {
      ...defaultSupportConfig.goal,
      ...(config.goal || {})
    },
    currencySymbol: config.currencySymbol ?? getSupportCurrencySymbol(resolvedCurrency),
    allowCustomAmount: true
  };
}
